import {
  CalendarMonthOutlined,
  ChevronRightOutlined,
  EventAvailableOutlined,
  EventBusyOutlined,
} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import FireDeadlineBadge from '@/components/ui/FireDeadlineBadge';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099FireToIrisMigration = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="FIRE to IRIS Migration Made Easy with TaxZerone"
          description="The IRS is retiring the FIRE system in December 2026. TaxZerone accepts your FIRE format, converts it to IRIS XML, validates records, and files automatically."
          keywords=""
          canonical="information-returns/form-1099-fire-to-iris-migration/"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="166"
        />
      }
    >
      <FireDeadlineBadge />
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ mt: 5, px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Box
              component="header"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                FIRE to{' '}
                <span className="highlight-container">
                  <span className="highlight">IRIS Migration</span>
                </span>{' '}
                Made Easy with{' '}
                <span className="highlight-container">
                  <span className="highlight">TaxZerone</span>
                </span>{' '}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Upload your existing FIRE (P1220) files while TaxZerone
                automatically converts them into IRIS format, validates the data
                for accuracy, and securely submits them to the IRS.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                No complex migration process. No manual rework. No filing
                disruptions
              </Typography>

              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 2 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Button
                  variant="contained"
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w9`}
                  sx={{
                    mt: { xs: 4, md: 4 },
                    fontSize: '22px !important',
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                >
                  Get started for FREE
                </Button>
              </Stack>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <picture style={{ marginTop: 50 }}>
                <source srcSet="/assets/images/fire-to-iris-migration-img.webp" />
                <source srcSet="/assets/images/fire-to-iris-migration-img.png" />
                <img
                  src="/assets/images/fire-to-iris-migration-img.png"
                  alt="fire-to-iris-migration"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginTop: 50 }}>
                <source srcSet="/assets/images/fire-to-iris-migration-img.webp" />
                <source srcSet="/assets/images/fire-to-iris-migration-img.png" />
                <img
                  src="/assets/images/fire-to-iris-migration-img.png"
                  alt="fire-to-iris-migration"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>
      <Box
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: { lg: 6, md: 6, sm: 5, xs: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mt: { xs: 1, md: 0 }, mb: { xs: 0, md: 0 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  What is the FIRE-to-IRIS transition?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  If your business files{' '}
                  <Link href="/form-1099-series/" sx={{ color: '#0202ff' }}>
                    1099s
                  </Link>{' '}
                  electronically, you have been using the IRS FIRE (Filing
                  Information Returns Electronically) system. The IRS is
                  shutting it down in{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/p1220.pdf"
                    sx={{ color: '#0202ff' }}
                  >
                    December 2026
                  </Link>
                  . After that, all electronic information returns must be filed
                  through IRIS (Information Returns Intake System), which uses a
                  different file format.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ md: 6, xs: 12 }} textAlign="center">
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  mt: { xs: 3, md: 0 },
                  mb: { xs: 3, md: 3 },
                  mr: { xs: 0, md: 0 },
                }}
              >
                <picture>
                  <source srcSet="/assets/images/fire-to-iris-transition.webp" />
                  <source srcSet="/assets/images/fire-to-iris-transition.png" />
                  <img
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                    }}
                    src="/assets/images/fire-to-iris-transition.png"
                    alt="What is the FIRE to IRIS transition?"
                    className="What is the FIRE to IRIS"
                    loading="lazy"
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          width: '100%',
          py: { xs: 3, sm: 5, md: 7 },
          // px: { xs: 1.5, sm: 2, md: 3 },
          backgroundColor: '#ffffff',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              width: '100%',
              py: {
                xs: 5,
                sm: 7,
                md: 10,
              },

              backgroundColor: '#ffffff',
            }}
          >
            <Box
              sx={{
                maxWidth: '1500px',
                mx: 'auto',
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: '1fr 90px 1fr',
                },
                alignItems: 'stretch',
                gap: {
                  xs: 3,
                  lg: 0,
                },
              }}
            >
              {/* LEFT CARD — BEFORE DECEMBER 2026 */}

              <Paper
                elevation={0}
                sx={{
                  position: 'relative',
                  border: '1px solid #dfe3e8',
                  borderRadius: {
                    xs: '16px',
                    md: '18px',
                  },
                  background:
                    'linear-gradient(135deg, #ffffff 0%, #fcfcfd 100%)',
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)',
                  p: {
                    xs: 3,
                    sm: 4,
                    md: 5,
                  },
                  // minHeight: {
                  //   xs: 'auto',
                  //   lg: '825px',
                  // },
                }}
              >
                {/* TOP CONTENT */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: {
                      xs: 2,
                      sm: 3,
                      md: 4,
                    },
                  }}
                >
                  {/* Calendar Icon */}
                  <Box
                    sx={{
                      width: {
                        xs: 90,
                        sm: 105,
                        md: 125,
                      },
                      height: {
                        xs: 90,
                        sm: 105,
                        md: 125,
                      },
                      minWidth: {
                        xs: 90,
                        sm: 105,
                        md: 125,
                      },
                      borderRadius: '50%',
                      background:
                        'linear-gradient(145deg, #f1f2f4 0%, #e7e9ec 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <CalendarMonthOutlined
                      sx={{
                        fontSize: {
                          xs: 48,
                          sm: 58,
                          md: 60,
                        },
                        color: '#687384',
                      }}
                    />

                    {/* Small blocked icon */}
                    <EventBusyOutlined
                      sx={{
                        position: 'absolute',
                        right: {
                          xs: 10,
                          sm: 12,
                          md: 14,
                        },
                        bottom: {
                          xs: 10,
                          sm: 12,
                          md: 14,
                        },
                        fontSize: {
                          xs: 28,
                          sm: 34,
                          md: 38,
                        },
                        color: '#687384',
                        backgroundColor: '#e9eaed',
                        borderRadius: '50%',
                      }}
                    />
                  </Box>

                  {/* Heading */}
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                        lineHeight: 1.35,
                        fontWeight: 700,
                        color: '#687384',
                      }}
                    >
                      FIRE — RETIRING
                      <br />
                      DECEMBER 2026
                    </Typography>
                  </Box>
                </Box>

                {/* HORIZONTAL TIMELINE */}
                <Box
                  sx={{
                    position: 'relative',
                    height: '2px',
                    backgroundColor: '#d6d9de',
                    mt: {
                      xs: 4,
                      md: 5,
                    },
                    mb: {
                      xs: 4,
                      md: 5,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: '-1px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: {
                        xs: '17px',
                        md: '20px',
                      },
                      height: {
                        xs: '17px',
                        md: '20px',
                      },
                      borderRadius: '50%',
                      backgroundColor: '#ffffff',
                      border: '3px solid #8d98a7',
                    },
                  }}
                />

                {/* METHOD LABEL */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: {
                      xs: 2,
                      md: 2.5,
                    },
                    py: {
                      xs: 0.7,
                      md: 0.9,
                    },
                    borderRadius: '30px',
                    background:
                      'linear-gradient(90deg, #e9eaec 0%, #e1e3e6 100%)',
                    mb: {
                      xs: 2.5,
                      md: 3,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: {
                        xs: 14,
                        sm: 16,
                        md: 18,
                      },
                      fontWeight: 700,
                      color: '#3f4b5d',
                      lineHeight: 1,
                    }}
                  >
                    FIRE METHOD
                  </Typography>
                </Box>

                {/* DESCRIPTION */}
                <Typography
                  sx={{
                    fontSize: {
                      xs: 16,
                      sm: 18,
                      md: 20,
                    },
                    lineHeight: 1.85,
                    color: '#17283f',
                    mb: {
                      xs: 3,
                      md: 4,
                    },
                  }}
                >
                  FIRE accepts fixed-width text files (.txt) with records
                  structured to IRS Publication 1220 specifications.
                </Typography>

                <Typography
                  sx={{
                    fontSize: {
                      xs: 16,
                      sm: 18,
                      md: 20,
                    },
                    lineHeight: 1.85,
                    color: '#17283f',
                  }}
                >
                  It has been the standard since the 1990s. Most in-house and
                  third-party payroll systems output this format natively.
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: { sm: 5, xs: 2 },
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note :</b> FIRE will not accept new submissions after
                  December 2026.
                </Alert>
              </Paper>

              {/* =====================================================
            CENTER TRANSITION
        ===================================================== */}

              <Box
                sx={{
                  position: 'relative',
                  display: {
                    xs: 'none',
                    lg: 'flex',
                  },
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '100%',
                }}
              >
                {/* Vertical dashed line */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '27%',
                    bottom: '14%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '2px dashed #c8ced7',
                  }}
                />

                {/* Top circle */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '26%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#ffffff',
                    border: '3px solid #aab3bf',
                    zIndex: 2,
                  }}
                />

                {/* Arrow Circle */}
                <Box
                  sx={{
                    width: 82,
                    height: 82,
                    borderRadius: '50%',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 5px 20px rgba(15, 23, 42, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 3,
                  }}
                >
                  <ChevronRightOutlined
                    sx={{
                      fontSize: 52,
                      color: '#1769d5',
                    }}
                  />
                </Box>

                {/* Bottom circle */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '13%',
                    left: '50%',
                    transform: 'translate(-50%, 50%)',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#1976ed',
                    zIndex: 2,
                  }}
                />
              </Box>

              {/* =====================================================
            RIGHT CARD — FROM JANUARY 2027
        ===================================================== */}

              <Paper
                elevation={0}
                sx={{
                  position: 'relative',
                  border: '1px solid #9bc4ff',
                  borderRadius: {
                    xs: '16px',
                    md: '18px',
                  },
                  background:
                    'linear-gradient(135deg, #ffffff 0%, #f7faff 100%)',
                  boxShadow: '0 2px 12px rgba(30, 111, 220, 0.04)',
                  p: {
                    xs: 3,
                    sm: 4,
                    md: 5,
                  },
                  // minHeight: {
                  //   xs: 'auto',
                  //   lg: '825px',
                  // },
                }}
              >
                {/* TOP CONTENT */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: {
                      xs: 2,
                      sm: 3,
                      md: 4,
                    },
                  }}
                >
                  {/* Calendar Icon */}
                  <Box
                    sx={{
                      width: {
                        xs: 90,
                        sm: 105,
                        md: 125,
                      },
                      height: {
                        xs: 90,
                        sm: 105,
                        md: 125,
                      },
                      minWidth: {
                        xs: 90,
                        sm: 105,
                        md: 125,
                      },
                      borderRadius: '50%',
                      background:
                        'linear-gradient(145deg, #edf5ff 0%, #dceaff 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <CalendarMonthOutlined
                      sx={{
                        fontSize: {
                          xs: 48,
                          sm: 58,
                          md: 70,
                        },
                        color: '#213e99',
                      }}
                    />

                    <EventAvailableOutlined
                      sx={{
                        position: 'absolute',
                        right: {
                          xs: 10,
                          sm: 12,
                          md: 14,
                        },
                        bottom: {
                          xs: 10,
                          sm: 12,
                          md: 14,
                        },
                        fontSize: {
                          xs: 28,
                          sm: 34,
                          md: 40,
                        },
                        color: '#213e99',
                        backgroundColor: '#e2eeff',
                        borderRadius: '50%',
                      }}
                    />
                  </Box>

                  {/* Heading */}
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                        lineHeight: 1.35,
                        fontWeight: 700,
                        color: '#213e99',
                      }}
                    >
                      IRIS — REQUIRED
                      <br />
                      FROM JANUARY 2027
                    </Typography>
                  </Box>
                </Box>

                {/* HORIZONTAL TIMELINE */}
                <Box
                  sx={{
                    position: 'relative',
                    height: '2px',
                    backgroundColor: '#2583ee',
                    mt: {
                      xs: 4,
                      md: 5,
                    },
                    mb: {
                      xs: 4,
                      md: 5,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: '-1px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: {
                        xs: '17px',
                        md: '20px',
                      },
                      height: {
                        xs: '17px',
                        md: '20px',
                      },
                      borderRadius: '50%',
                      backgroundColor: '#2583ee',
                    },
                  }}
                />

                {/* METHOD LABEL */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: {
                      xs: 2,
                      md: 2.5,
                    },
                    py: {
                      xs: 0.7,
                      md: 0.9,
                    },
                    borderRadius: '30px',
                    background:
                      'linear-gradient(90deg, #e2efff 0%, #d7e8ff 100%)',
                    mb: {
                      xs: 2.5,
                      md: 3,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: {
                        xs: 14,
                        sm: 16,
                        md: 18,
                      },
                      fontWeight: 700,
                      color: '#213e99',
                      lineHeight: 1,
                    }}
                  >
                    IRIS METHOD
                  </Typography>
                </Box>

                {/* DESCRIPTION */}
                <Typography
                  sx={{
                    fontSize: {
                      xs: 16,
                      sm: 18,
                      md: 20,
                    },
                    lineHeight: 1.85,
                    color: '#17283f',
                    mb: {
                      xs: 3,
                      md: 4,
                    },
                  }}
                >
                  IRIS filing may require an IRIS TCC depending on whether you
                  file directly or through a third-party transmitter.
                </Typography>

                <Typography
                  sx={{
                    fontSize: {
                      xs: 16,
                      sm: 18,
                      md: 20,
                    },
                    lineHeight: 1.85,
                    color: '#17283f',
                  }}
                >
                  TaxZerone can help guide or support the applicable TCC-based
                  workflow.
                </Typography>

                {/* Note */}
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: { sm: 5, xs: 2 },
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note :</b> Your existing FIRE files cannot be submitted to
                  IRIS without conversion. 2026.
                </Alert>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Box>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: { xs: 6, md: 10 },
        }}
        m={0}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={{ xs: 5, md: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 700,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.3,
                letterSpacing: '-0.02em',
              }}
            >
              From FIRE file to IRIS Submission in three steps
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                textAlign: 'center',
                maxWidth: 620,
                mx: 'auto',
                lineHeight: 1.6,
                mb: -2,
              }}
            >
              No new systems to learn. No engineering sprints. TaxZerone fits
              into how your team already works.
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="stretch">
            <Grid size={{ xs: 12, md: 4 }} display="flex">
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#f8fafc',
                  borderRadius: 3,
                  p: 3.5,
                  borderLeft: '4px solid #1da94a',
                  borderTop: '1px solid #e2e8f0',
                  borderRight: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    boxShadow: '0 12px 24px -6px rgba(15, 23, 42, 0.08)',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <picture>
                    <source
                      srcSet="/assets/images/step-1-icon.webp"
                      width={36}
                      height={36}
                    />
                    <source
                      srcSet="/assets/images/step-1-icon.png"
                      width={36}
                      height={36}
                    />
                    <img
                      src="/assets/images/step-1-icon.png"
                      alt="Upload FIRE file"
                      loading="lazy"
                      width={36}
                      height={36}
                    />
                  </picture>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 17,
                      color: '#0f172a',
                      lineHeight: 1.3,
                    }}
                  >
                    Upload Your FIRE file
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.65,
                    fontSize: 14.5,
                    flexGrow: 1,
                  }}
                >
                  Upload the same Publication 1220 fixed-width text files you're
                  already generating.
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }} display="flex">
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#f8fafc',
                  borderRadius: 3,
                  p: 3.5,
                  borderLeft: '4px solid #1da94a',
                  borderTop: '1px solid #e2e8f0',
                  borderRight: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    boxShadow: '0 12px 24px -6px rgba(15, 23, 42, 0.08)',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <picture>
                    <source
                      srcSet="/assets/images/step-2-icon.webp"
                      width={36}
                      height={36}
                    />
                    <source
                      srcSet="/assets/images/step-2-icon.png"
                      width={36}
                      height={36}
                    />
                    <img
                      src="/assets/images/step-2-icon.png"
                      alt="Convert to IRIS XML"
                      loading="lazy"
                      width={36}
                      height={36}
                    />
                  </picture>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 17,
                      color: '#0f172a',
                      lineHeight: 1.3,
                    }}
                  >
                    Convert FIRE Files to IRIS XML
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.65,
                    fontSize: 14.5,
                    flexGrow: 1,
                  }}
                >
                  TaxZerone converts your FIRE data into IRIS-compatible XML and
                  validates every record for accuracy. Errors are flagged before
                  submission so you can review and correct them in advance.
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }} display="flex">
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#f8fafc',
                  borderRadius: 3,
                  p: 3.5,
                  borderLeft: '4px solid #1da94a',
                  borderTop: '1px solid #e2e8f0',
                  borderRight: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    boxShadow: '0 12px 24px -6px rgba(15, 23, 42, 0.08)',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <picture>
                    <source
                      srcSet="/assets/images/step-3-icon.webp"
                      width={36}
                      height={36}
                    />
                    <source
                      srcSet="/assets/images/step-3-icon.png"
                      width={36}
                      height={36}
                    />
                    <img
                      src="/assets/images/step-3-icon.png"
                      alt="Secure IRS Submission"
                      loading="lazy"
                      width={36}
                      height={36}
                    />
                  </picture>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 17,
                      color: '#0f172a',
                      lineHeight: 1.3,
                    }}
                  >
                    Secure IRS Submission
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.65,
                    fontSize: 14.5,
                    flexGrow: 1,
                  }}
                >
                  Your validated filings are securely submitted to the IRS
                  through the IRIS system. You receive real-time confirmation
                  and a tracking record for every submission.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box textAlign="center" mt={6}>
            <Button
              variant="contained"
              className="btn-primary"
              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w9`}
              sx={{
                mr: { xs: 0, md: 4 },
                transition: 'all 0.3s ease 0s',
                '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
              }}
            >
              Get Started Now
            </Button>
          </Box>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Every Major information return, covered
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 0,
                  maxWidth: '600px',
                  mx: 'auto',
                  textAlign: 'center',
                }}
              >
                Confirm your specific forms are supported before you start.
                TaxZerone handles the full range of IRS information returns
                filed through FIRE today.
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            m={2}
            mt={2}
            py={{ xs: 0, sm: 4, md: 7 }}
            className="MuiGrid-root-width"
            justifyContent="center"
          >
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-nec-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-nec-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-nec-icon.svg"
                      alt="1099-NEC"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-nec-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-NEC
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Nonemployee compensation
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-misc-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-misc-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-misc-icon.svg"
                      alt="1099-MISC"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-misc-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-MISC
                  </Link>
                </Typography>

                <Typography variant="body1">Miscellaneous income</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-int-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-int-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-int-icon.svg"
                      alt="1099-INT"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-int-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-INT
                  </Link>
                </Typography>

                <Typography variant="body1">Interest income</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-div-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-div-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-div-icon.svg"
                      alt="1099-DIV"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-div-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-DIV
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Dividends and distributions
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-b-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-b-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-b-icon.svg"
                      alt="1099-B"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-b-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-B
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Proceeds from broker transactions
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-r-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-r-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-r-icon.svg"
                      alt="1099-R"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-r-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-R
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Distributions from pensions &amp; IRAs
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-k-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-k-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-k-icon.svg"
                      alt="1099-K"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-k-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-K
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Payment card &amp; third-party network transactions
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-s-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-s-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-s-icon.svg"
                      alt="1099-S"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-s-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-S
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Proceeds from real estate transactions
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-g-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-g-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-g-icon.svg"
                      alt="1099-G"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-g-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-G
                  </Link>
                </Typography>

                <Typography variant="body1">Government payments</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-oid-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-oid-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-oid-icon.svg"
                      alt="1099-OID"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-oid-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-OID
                  </Link>
                </Typography>

                <Typography variant="body1">Original issue discount</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-patr-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-patr-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-patr-icon.svg"
                      alt="1099-PATR"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-patr-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-PATR
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Taxable distributions from cooperatives
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1099-sa-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1099-sa-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1099-sa-icon.svg"
                      alt="1099-SA"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1099-sa-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-SA
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Distributions from HSA/MSA
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/w2-g-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/w2-g-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/w2-g-icon.svg"
                      alt="W-2G"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-w-2g-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    W-2G
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Certain gambling winnings
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1042-s-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1042-s-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1042-s-icon.svg"
                      alt="1042-S"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1042-s-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1042-S
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Foreign person's US source income
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/5498-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/5498-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/5498-icon.svg"
                      alt="5498"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-5498-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    5498
                  </Link>
                </Typography>

                <Typography variant="body1">
                  IRA contribution information
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1098-c-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1098-c-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1098-c-icon.svg"
                      alt="1098-C"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1098-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1098-C
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Contributions of Motor Vehicles, Boats, and Airplanes
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1098-f-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1098-f-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1098-f-icon.svg"
                      alt="1098-F"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1098-f-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1098-F
                  </Link>
                </Typography>

                <Typography variant="body1">Fines Penalties</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1098-q-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1098-q-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1098-q-icon.svg"
                      alt="1098-Q"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1098-q-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1098-Q
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Qualifying longevity Annuity Contract Information
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/1098-t-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/1098-t-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/1098-t-icon.svg"
                      alt="1098-T"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-1098-t-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1098-T
                  </Link>
                </Typography>

                <Typography variant="body1">Tuition Statement</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/5498-esa-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/5498-esa-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/5498-esa-icon.svg"
                      alt="5498-ESA"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-5498-esa-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    5498-ESA
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Coverdell ESA Contribution Information
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/5498-sa-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/5498-sa-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/5498-sa-icon.svg"
                      alt="5498-SA"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/resources/information-returns/form-5498-sa-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    5498-SA
                  </Link>
                </Typography>

                <Typography variant="body1">
                  HSA, Archer MSA, or Medicare Advantage MSA Information
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/3921-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/3921-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/3921-icon.svg"
                      alt="3921"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/information-returns/efile-irs-form-3921/"
                    sx={{ color: '#0202ff' }}
                  >
                    3921
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Exercise of an Incentive Stock Option Under Section 422(b)
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/3922-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/3922-icon.svg"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/3922-icon.svg"
                      alt="3922"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  <Link
                    href="/information-returns/efile-irs-form-3922/"
                    sx={{ color: '#0202ff' }}
                  >
                    3922
                  </Link>
                </Typography>

                <Typography variant="body1">
                  Transfer of Stock Acquired Through an Employee Stock Purchase
                  Plan Under Section 423(c)
                </Typography>
              </Paper>
            </Grid>
            <Typography variant="body1">
              Don't see your form?{' '}
              <Link href="/contact-us/" sx={{ color: '#0202ff' }}>
                Contact us
              </Link>{' '}
              — we support additional form types not listed here.
            </Typography>
          </Grid>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                What the FIRE Shutdown Means for you?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 0,
                  maxWidth: '600px',
                  mx: 'auto',
                  textAlign: 'center',
                }}
              >
                Without a transition plan, the IRS deadline creates real
                engineering cost, filing risk, and penalty exposure. Here's the
                difference TaxZerone makes.
              </Typography>
            </Grid>
          </Grid>
          <TableContainer
            sx={{
              border: '1px solid #e2e8f0',
              overflowX: {
                xs: 'auto',
                md: 'unset',
              },
              background: '#ffffff',
              boxShadow: '0 8px 40px rgba(0, 0, 0, 0.2)',
              mx: 'auto',
              width: '100%',
              // borderRadius: '12px',
              overflow: 'hidden',
              mb: 5,
            }}
          >
            <Box sx={{ minWidth: '840px' }}>
              <Table
                sx={{
                  minWidth: 700,
                  borderCollapse: 'collapse',
                  width: '100%',

                  '& thead': {
                    background: '#ffffff',
                  },

                  '& thead th': {
                    fontSize: {
                      xs: '11px',
                      sm: '12px',
                      md: '13px',
                    },
                    fontWeight: 600,
                    textAlign: 'left',
                    padding: {
                      xs: '10px 8px',
                      sm: '14px 16px',
                      md: '16px 20px',
                    },
                    whiteSpace: 'nowrap',
                  },

                  '& tbody tr': {
                    transition: 'background-color 0.15s ease',
                  },

                  '& tbody tr:nth-of-type(even)': {
                    backgroundColor: '#f8fafc',
                  },

                  '& tbody td': {
                    fontSize: {
                      xs: '12px',
                      sm: '13.5px',
                      md: '14.5px',
                    },
                    fontWeight: 400,
                    textAlign: 'left',
                    padding: {
                      xs: '10px 8px',
                      sm: '13px 16px',
                      md: '13px 20px',
                    },
                    color: '#1e293b',
                    borderBottom: 'none',
                    transition: 'color 0.15s ease',
                  },

                  '& tbody td:first-of-type': {
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    color: '#0f172a',
                  },

                  '& tbody tr:last-of-type td': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* Challenge */}
                    <TableCell
                      sx={{
                        background: '#fff',
                        borderBottom: 'none',
                        position: 'relative',
                        paddingBottom: '20px',

                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '85%',
                          height: '1.5px',
                          backgroundColor: '#e2e8f0',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          fontWeight: 700,
                        }}
                      >
                        Challenge
                      </Box>
                    </TableCell>

                    {/* With TaxZerone */}
                    <TableCell
                      sx={{
                        background: '#ffffff',
                        textAlign: 'center',
                        color: '#213e99',
                        border: '2px solid #213e99',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: 12, sm: 14, md: 16 },
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          With TaxZerone
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Without TaxZerone */}
                    <TableCell
                      sx={{
                        background: '#fff',
                        borderBottom: 'none',
                        position: 'relative',
                        paddingBottom: '20px',

                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '85%',
                          height: '1.5px',
                          backgroundColor: '#e2e8f0',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: 12, sm: 14, md: 16 },
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            color: '#0f172a',
                          }}
                        >
                          Without TaxZerone
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {/* System changes */}
                  <TableRow>
                    <TableCell>System changes</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Compatible with your existing FIRE workflow
                    </TableCell>

                    <TableCell>
                      Requires updates or rebuild of existing FIRE filing
                      systems
                    </TableCell>
                  </TableRow>

                  {/* Engineering effort */}
                  <TableRow>
                    <TableCell>Engineering effort</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Significantly reduced engineering effort for IRIS
                      transition
                    </TableCell>

                    <TableCell>
                      High developer involvement for IRIS transition
                    </TableCell>
                  </TableRow>

                  {/* File Handling */}
                  <TableRow>
                    <TableCell>File Handling</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Uses existing FIRE files as-is
                    </TableCell>

                    <TableCell>
                      FIRE fixed-width files must be manually adapted
                    </TableCell>
                  </TableRow>

                  {/* Conversion Process */}
                  <TableRow>
                    <TableCell>Conversion Process</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Automatic FIRE → IRIS XML conversion
                    </TableCell>

                    <TableCell>
                      Manual or error-prone conversion to IRIS format
                    </TableCell>
                  </TableRow>

                  {/* XML Validation */}
                  <TableRow>
                    <TableCell>XML Validation</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Built-in IRIS XML validation for every record
                    </TableCell>

                    <TableCell>
                      Manual checks or third-party tools needed
                    </TableCell>
                  </TableRow>

                  {/* IRIS TCC */}
                  <TableRow>
                    <TableCell>IRIS TCC</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Guidance/support for IRIS TCC-based submission workflow
                    </TableCell>

                    <TableCell>
                      Businesses must manage Transmitter Control Code setup
                      separately
                    </TableCell>
                  </TableRow>

                  {/* State Filing */}
                  <TableRow>
                    <TableCell>State Filing</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Streamlined workflow for federal + state alignment
                    </TableCell>

                    <TableCell>
                      Separate handling required for state + federal filings
                    </TableCell>
                  </TableRow>

                  {/* IRS Submission */}
                  <TableRow>
                    <TableCell>IRS Submission</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Direct submission to the Internal Revenue Service via IRIS
                    </TableCell>

                    <TableCell>
                      Manual upload or separate tools required
                    </TableCell>
                  </TableRow>

                  {/* Error Detection */}
                  <TableRow>
                    <TableCell>Error Detection</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Issues flagged before submission during validation
                    </TableCell>

                    <TableCell>
                      Errors often found after submission or IRS rejection
                    </TableCell>
                  </TableRow>

                  {/* Timeline */}
                  <TableRow>
                    <TableCell>Timeline</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        fontWeight: 600,
                      }}
                    >
                      Login, upload and file — same day
                    </TableCell>

                    <TableCell>
                      Takes 6–12 months for development, testing and deployment
                    </TableCell>
                  </TableRow>

                  {/* Penalty Exposure */}
                  <TableRow>
                    <TableCell>Penalty Exposure</TableCell>

                    <TableCell
                      sx={{
                        borderLeft: '2px solid #213e99',
                        borderRight: '2px solid #213e99',
                        borderBottom: '2px solid #213e99 !important',
                        fontWeight: 600,
                      }}
                    >
                      File through IRIS on time to reduce compliance risk and
                      avoid disruptions.
                    </TableCell>

                    <TableCell>
                      Late filings can result in IRS penalties of $60–$340 per
                      form
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </Container>
      </Grid>

      <Box
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: { lg: 6, md: 6, sm: 5, xs: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column', md: 'row' } }}
          >
            <Grid size={{ md: 6, xs: 12 }} textAlign="center">
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  mt: { xs: 3, md: 0 },
                  mb: { xs: 3, md: 3 },
                  // mr: { xs: 0, md: 0 },
                  // maxHeight: 432,
                }}
              >
                <picture>
                  <source srcSet="/assets/images/built-by-tax-filing-specialists.webp" />
                  <source srcSet="/assets/images/built-by-tax-filing-specialists.png" />
                  <img
                    src="/assets/images/built-by-tax-filing-specialists.png"
                    alt="Built by tax filing specialists, not generalists"
                    loading="lazy"
                    width="100%"
                    // height="{auto}"
                  />
                </picture>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mt: { xs: 1, md: 0 }, mb: { xs: 0, md: 0 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Built by tax filing specialists, not generalists
                </Typography>

                <Typography component="div" variant="body1" sx={{ mb: 3 }}>
                  <List
                    sx={{
                      display: 'inline-block',
                      mb: 2,
                    }}
                  >
                    <ListItem
                      disablePadding
                      alignItems="flex-start"
                      sx={{ mb: 1 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 2, mt: 0.5 }}
                      >
                        <CheckCircleIcon sx={{ color: '#1da94a' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <>
                            <span style={{ fontWeight: 600 }}>
                              Trusted experience in IRS e-filing
                            </span>
                            <Typography variant="body1">
                              Exercise of an Incentive Stock Option Under
                              Section 422(b)
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>

                    <ListItem
                      disablePadding
                      alignItems="flex-start"
                      sx={{ mb: 1 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 2, mt: 0.5 }}
                      >
                        <CheckCircleIcon sx={{ color: '#1da94a' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <>
                            <span style={{ fontWeight: 600 }}>
                              SOC 2 Type II certified infrastructure
                            </span>
                            <Typography variant="body1">
                              TaxZerone undergoes independent third-party audits
                              of its security controls. Your taxpayer data and
                              filing records are protected by the same standards
                              used by enterprise financial services providers.
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <ListItem
                      disablePadding
                      alignItems="flex-start"
                      sx={{ mb: 1 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 2, mt: 0.5 }}
                      >
                        <CheckCircleIcon sx={{ color: '#1da94a' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <>
                            <span style={{ fontWeight: 600 }}>
                              50,000+ businesses have filed through our platform
                            </span>
                            <Typography variant="body1">
                              From mid-market enterprises to payroll bureaus
                              handling millions of forms, our platform has
                              processed large-volume FIRE submissions at scale —
                              and is fully equipped for IRIS at the same volume.
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <ListItem
                      disablePadding
                      alignItems="flex-start"
                      sx={{ mb: 1 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 2, mt: 0.5 }}
                      >
                        <CheckCircleIcon sx={{ color: '#1da94a' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <>
                            <span style={{ fontWeight: 600 }}>
                              Active IRS compliance monitoring
                            </span>
                            <Typography variant="body1">
                              Our compliance team tracks IRS schema updates, TCC
                              policy changes, and IRIS release notes
                              continuously. When the IRS updates its
                              specifications, your filings stay accurate without
                              any action from you.
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  </List>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg" sx={{ justifyContent: 'center', my: 3 }}>
          <Grid
            container
            spacing={2}
            m={0}
            mt={4}
            mb={6}
            className="MuiGrid-root-width"
            justifyContent="center"
          >
            <Grid
              size={{ xs: 12, md: 12 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'center',
                  md: 'center',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Everything Handled. Nothing Rebuilt
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  Top reasons businesses choose TaxZerone to manage their IRIS
                  transition without disruption.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  // borderWidth: 3,
                  // borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  No Changes to Your Existing System
                </Typography>
                <Typography variant="body1" sx={{ mt: 5 }}>
                  Your FIRE file generation process stays exactly as-is.
                  TaxZerone works directly with the output your current system
                  already produces — no modifications, no rebuild, no new
                  dependencies
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  // borderWidth: 3,
                  // borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Seamless FIRE to IRIS Conversion
                </Typography>
                <Typography variant="body1">
                  TaxZerone automatically converts FIRE fixed-width files into
                  valid IRIS XML on every upload. No manual mapping, no
                  spreadsheet gymnastics, no third-party conversion tools
                  required.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  // borderWidth: 3,
                  // borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  First Used Month (FUM) of a vehicleBuilt-in Validation for
                  Every Record
                </Typography>
                <Typography variant="body1">
                  Every record is checked against IRS schema rules before
                  submission. Errors and missing fields are flagged early so
                  your team can review and correct them before a rejection ever
                  occurs.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  // borderWidth: 3,
                  // borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Direct, Secure IRS Submission
                </Typography>
                <Typography variant="body1">
                  Files are submitted directly to the Internal Revenue Service
                  through the IRIS system. You receive real-time acknowledgment
                  and a full submission record for every batch filed
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  // borderWidth: 3,
                  // borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Reduced Compliance Risk
                </Typography>
                <Typography variant="body1" sx={{ mt: 5 }}>
                  Automated validation, structured processing, and an
                  always-current IRS schema mean fewer filing errors, fewer late
                  submissions, and a dramatically lower risk of the $60–$340
                  per-form penalty
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  // borderWidth: 3,
                  // borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Expert Support When You Need It
                </Typography>
                <Typography variant="body1" sx={{ mt: 5 }}>
                  Get access to dedicated support throughout your IRIS
                  transition. TaxZerone’s experts help answer technical
                  questions, resolve filing issues, and ensure your migration
                  stays smooth from start to finish.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          // backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Who uses TaxZerone for IRIS Migration?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  mx: 'auto',
                  maxWidth: { sm: '80%', xs: 'auto' },
                  textAlign: 'center',
                }}
              >
                TaxZerone serves businesses and service providers that file
                information returns at volume and need a clean, low-risk path to
                IRIS compliance.
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="center"
            spacing={2}
            m={2}
            mt={4}
            pb={{ xs: 0, sm: 4, md: 7 }}
          >
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/profile-business-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/profile-business-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/profile-business-icon.png"
                      alt="profile-business"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Larger Businesses & Enterprises
                </Typography>
                <Typography variant="body1">
                  Large organizations filing 1099s, forms at scale, with
                  dedicated payroll or finance teams.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/mid-businesses-icon.webp"
                      width={55}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/mid-businesses-icon.png"
                      width={55}
                      height={60}
                    />
                    <img
                      src="/assets/images/mid-businesses-icon.png"
                      alt="Mid size businesses"
                      loading="lazy"
                      width={55}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Mid-size businesses
                </Typography>
                <Typography variant="body1">
                  Growing businesses with increasing filing volumes that need a
                  reliable way to transition from FIRE to IRIS without investing
                  in costly system upgrades or engineering resources.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/sale-tax-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/sale-tax-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/sale-tax-icon.png"
                      alt="Payroll"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Payroll & Tax Bureaus
                </Typography>
                <Typography variant="body1">
                  Service bureaus processing returns on behalf of multiple
                  clients who need bulk FIRE-to-IRIS handling
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 50, height: 50 }}>
                    <source
                      srcSet="/assets/images/industries-icon.webp"
                      width={50}
                      height={50}
                    />
                    <source
                      srcSet="/assets/images/industries-icon.png"
                      width={50}
                      height={50}
                    />
                    <img
                      src="/assets/images/industries-icon.png"
                      alt="Accounting and CPA Firms"
                      loading="lazy"
                      width={50}
                      height={50}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Accounting & CPA Firms
                </Typography>
                <Typography variant="body1">
                  CPA and tax advisory firms managing information return filings
                  for business clients across industries.
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/obligation-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/obligation-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/obligation-icon.png"
                      alt="Tax Filing Service"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Tax Filing Service Providers
                </Typography>
                <Typography variant="body1">
                  Third-party tax service providers managing electronic filing
                  for multiple clients who need scalable, compliant, and
                  automated IRIS submission workflows.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 50, height: 50 }}>
                    <source
                      srcSet="/assets/images/bank-icon.webp"
                      width={50}
                      height={50}
                    />
                    <source
                      srcSet="/assets/images/bank-icon.png"
                      width={50}
                      height={50}
                    />
                    <img
                      src="/assets/images/bank-icon.png"
                      alt="Financial Institutions"
                      loading="lazy"
                      width={50}
                      height={50}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Banks & Financial Institutions
                </Typography>
                <Typography variant="body1">
                  Banks, credit unions, and brokerages filing 1099-INT,
                  1099-DIV, 1099-B at high volume with strict IRS formatting
                  requirements.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/transmit-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/transmit-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/transmit-icon.png"
                      alt="Software Vendors"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Software Vendors
                </Typography>
                <Typography variant="body1">
                  ISVs and SaaS platforms whose products output FIRE files. Add
                  IRIS submission without rebuilding your filing pipeline or
                  managing an IRS TCC yourself
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/late-filing-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/late-filing-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/late-filing-icon.png"
                      alt="Direct FIRE Filers"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Direct FIRE Filers
                </Typography>
                <Typography variant="body1">
                  Businesses that have filed directly with the IRS through FIRE
                  for years and need a straight replacement before the December
                  2026 shutdown.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2 }}
                justifyContent={{ xs: 'center', md: 'center' }}
              >
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-1"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-2"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-3"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-4"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-5"
                />
              </Stack>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 1 },
                }}
              >
                Trusted by Over 50,000 Businesses
              </Typography>

              <Grid
                container
                spacing={2}
                m={0}
                justifyContent="center"
                direction="row"
                className="MuiGrid-root-width"
                sx={{ mb: 3 }}
              >
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#ffffff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                      minHeight={{ xs: 'auto', sm: 'auto', md: 200, lg: 200 }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        We handle a large number of 1099 filings for our clients
                        every year, so we expected the move to IRIS to be
                        complicated. TaxZerone made it simple. We uploaded the
                        same FIRE files we already use, and TaxZerone took care
                        of the rest
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      - Micheal Smith
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: 'right', maxWidth: '200px', ml: 'auto' }}
                    >
                      Filing Operations, Large Payroll Bureau
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#ffffff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                      minHeight={{ xs: 'auto', sm: 'auto', md: 200, lg: 200 }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        We thought moving to IRIS would take a lot of time and
                        effort. TaxZerone made the transition smooth and easy.
                        The built-in validation helped us catch errors early and
                        avoid filing rejections.
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      -Sandra Davis
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{ textAlign: 'right', maxWidth: '200px', ml: 'auto' }}
                    >
                      VP Finance, Mid-Market Enterprise
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#ffffff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                      minHeight={{ xs: 'auto', sm: 'auto', md: 200, lg: 200 }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        The move from FIRE to IRIS felt complicated at first.
                        TaxZerone made everything simple. We could upload files,
                        fix errors quickly, and complete filings without any
                        stress
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      -David Willimas
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: 'right', maxWidth: '200px', ml: 'auto' }}
                    >
                      Partner, CPA & Tax Advisory Firm
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          The FIRE deadline is December 2026. Your transition can start today.
        </Typography>
        <Typography
          variant="body1"
          color="white"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mb: 2,
            mx: 'auto',
            maxWidth: { sm: '50%', xs: 'auto' },
          }}
        >
          Login, upload your existing FIRE files, and file through IRIS —
          without touching your current system or waiting on engineering.
        </Typography>

        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w9`}
          >
            Get started with TaxZerone
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  How do I migrate from FIRE to IRIS?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Migrating from FIRE to IRIS with TaxZerone is simple. You
                    can continue using your existing FIRE files while TaxZerone
                    handles the transition for you. Our platform automatically
                    converts your FIRE fixed-width files into IRIS-compliant
                    XML, validates each record against IRS requirements, and
                    securely submits them through the IRIS system. This
                    eliminates manual rework, reduces compliance risk, and
                    ensures a smooth, disruption-free migration.
                  </Typography>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  What is the difference between FIRE and IRIS?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    The main difference between FIRE and IRIS is the file
                    format, technology, and filing process.
                  </Typography>

                  <ul style={{ marginBottom: 12 }}>
                    <li style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 600 }}>
                        FIRE (Filing Information Returns Electronically)
                      </span>{' '}
                      is the IRS’s legacy filing system that uses fixed-width
                      text files for submitting information returns.
                    </li>

                    <li style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 600 }}>
                        IRIS (Information Returns Intake System)
                      </span>{' '}
                      is the newer IRS filing platform that uses XML-based
                      structured files for electronic submissions.
                    </li>
                  </ul>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  Do I need a new TCC for IRIS filing?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    IRIS TCC requirements depend on your filing setup. If you
                    file directly with the IRS, you may need an IRIS Transmitter
                    Control Code. If you file through TaxZerone as a supported
                    filing workflow, TaxZerone can help guide you through the
                    applicable TCC-based submission process and clarify what
                    registration steps, if any, apply to your account.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  How long does FIRE to IRIS migration take?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    With TaxZerone, you can complete FIRE to IRIS migration in
                    just <span style={{ fontWeight: 600 }}>2 minutes</span>.
                    TaxZerone automatically converts your FIRE files into
                    IRIS-compatible XML, validates the data for accuracy, and
                    prepares it for IRS submission instantly. This eliminates
                    manual conversion steps, system changes, and long setup
                    time, making the entire migration process fast and seamless.
                  </Typography>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  What happens if I don’t switch from FIRE to IRIS?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    If you do not transition before FIRE is retired, you may not
                    be able to electronically file information returns with the
                    IRS, which can result in penalties and compliance issues.
                  </Typography>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  How much does it cost to migrate from FIRE to IRIS?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    With TaxZerone, you can migrate from FIRE to IRIS and start
                    filing at <span style={{ fontWeight: 600 }}>FREE</span>.
                    There is no need for expensive system rebuilds, custom
                    development, or manual conversion work.
                  </Typography>
                </Box>
              </Box>
            </Box>
            {/* <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  borderWidth: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover, &:focus, &:active': { borderWidth: 2 },
                }}
                href="/faqs/form-w9/"
              >
                More FAQs
              </Button>
            </Box> */}
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form1099FireToIrisMigration;
